import React from 'react';
import { Plus, Edit, Trash2, Users, Eye, Percent, Camera } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import companyStore from '../../store/company-store';

// Interface déjà importée depuis le modèle

interface CompanyListProps {
  onEdit: (companyId: string) => void;
  onDelete: (companyId: string) => void;
  onAdd: () => void;
  onView: (companyId: string) => void;
  onViewCollaborators: (companyId: string) => void;
}

const CompanyList: React.FC<CompanyListProps> = observer(({ onEdit, onDelete, onAdd, onView, onViewCollaborators }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Utilisation du store pour récupérer les entreprises
  const companies = companyStore.getCompanies;

  const handleViewPromoCodes = (companyId: string) => {
    navigate(`/${i18n.language}/provider/promo-codes/${companyId}`);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">{t('company.list.title', 'Mes entreprises')}</h2>
        <button 
          onClick={onAdd}
          className="flex items-center gap-2 px-3 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
        >
          <Plus size={16} />
          <span>{t('company.list.addNew', 'Ajouter')}</span>
        </button>
      </div>
      
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                {t('company.list.company', 'Entreprise')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                {t('company.list.collaborators', 'Collaborateurs')}
              </th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                {t('company.list.view', 'Voir')}
              </th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                {t('company.list.promoCodes', 'Promos')}
              </th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                {t('company.list.edit', 'Modifier')}
              </th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                {t('company.list.delete', 'Supprimer')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {companies.map((company) => (
              <tr key={company.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      {company.companyLogo ? (
                        <img
                          className="h-10 w-10 rounded-full object-cover"
                          src={company.companyLogo}
                          alt={company.businessName}
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                          <Camera size={20} className="text-gray-500 dark:text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{company.businessName}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => onViewCollaborators(company.id)}
                    className="flex items-center gap-1 text-sm text-primary hover:underline"
                  >
                    <Users size={16} />
                    <span>{company.collaboratorsCount || '0'}</span>
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <button
                    onClick={() => onView(company.id)}
                    className="inline-flex items-center justify-center p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-700 rounded-md transition-colors"
                    title={String(t('company.list.view', 'Visualiser'))}
                  >
                    <Eye size={20} />
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  {companyStore.isUserOwnerOfCompany(company) ? (
                    <button
                      onClick={() => handleViewPromoCodes(company.id)}
                      className="inline-flex items-center justify-center p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 dark:text-purple-400 dark:hover:text-purple-300 dark:hover:bg-purple-900/20 rounded-md transition-colors"
                      title={String(t('company.list.promoCodes', 'Codes Promo'))}
                    >
                      <Percent size={20}/>
                    </button>
                  ) : (
                    <span className="text-gray-300 dark:text-gray-600">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  {companyStore.isUserOwnerOfCompany(company) ? (
                    <button
                      onClick={() => onEdit(company.id)}
                      className="inline-flex items-center justify-center p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/20 rounded-md transition-colors"
                      title={String(t('company.list.edit', 'Modifier'))}
                    >
                      <Edit size={20}/>
                    </button>
                  ) : (
                    <span className="text-gray-300 dark:text-gray-600">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  {companyStore.isUserOwnerOfCompany(company) ? (
                    <button
                      onClick={() => onDelete(company.id)}
                      className="inline-flex items-center justify-center p-2 text-red-600 hover:text-red-800 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20 rounded-md transition-colors"
                      title={String(t('company.list.delete', 'Supprimer'))}
                    >
                      <Trash2 size={20}/>
                    </button>
                  ) : (
                    <span className="text-gray-300 dark:text-gray-600">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default CompanyList;
